import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Лёгкий брендовый tooltip — мгновенный показ (~150мс задержка).
 * Рендерится через React portal в document.body с position: fixed —
 * не обрезается родительскими overflow:auto/hidden (важно для скроллируемых колонок календаря).
 * Сторона — по реальному размеру подсказки: не влезает сверху — показываем снизу
 * (длинные тексты вроде сводки заказа Flowwow уходили за верх экрана); по
 * горизонтали прижимаем к краям окна, стрелка остаётся над элементом.
 */
function Tooltip({ text, children, delay = 150 }) {
  const [state, setState] = useState({ visible: false, x: 0, y: 0, placement: 'top', arrowShift: 0 });
  const timerRef = useRef(null);
  const wrapRef = useRef(null);
  const tipRef = useRef(null);

  useLayoutEffect(() => {
    if (!state.visible || !tipRef.current || !wrapRef.current) return;
    const tip = tipRef.current.getBoundingClientRect();
    const rect = wrapRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const spaceAbove = rect.top - 8;
    const spaceBelow = vh - rect.bottom - 8;
    let placement = state.placement;
    if (placement === 'top' && tip.height > spaceAbove && spaceBelow > spaceAbove) placement = 'bottom';
    if (placement === 'bottom' && tip.height > spaceBelow && spaceAbove > spaceBelow) placement = 'top';
    const anchor = rect.left + rect.width / 2;
    const half = tip.width / 2;
    const x = Math.min(Math.max(anchor, half + 8), vw - half - 8);
    const y = placement === 'top' ? rect.top - 8 : rect.bottom + 8;
    if (placement !== state.placement || Math.abs(x - state.x) > 0.5 || y !== state.y) {
      setState(st => ({ ...st, placement, x, y, arrowShift: anchor - x }));
    }
  }, [state.visible, state.placement, state.x, state.y]);

  if (!text) return children;

  const show = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      const placement = rect.top < 80 ? 'bottom' : 'top';
      const x = rect.left + rect.width / 2;
      const y = placement === 'top' ? rect.top - 8 : rect.bottom + 8;
      setState({ visible: true, x, y, placement, arrowShift: 0 });
    }, delay);
  };
  const hide = () => {
    clearTimeout(timerRef.current);
    setState(s => ({ ...s, visible: false }));
  };

  return (
    <>
      <span
        ref={wrapRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        style={{ display: 'inline-flex', alignItems: 'center' }}
      >
        {children}
      </span>
      {state.visible && createPortal(
        <span
          ref={tipRef}
          role="tooltip"
          style={{
            position: 'fixed',
            left: state.x,
            top: state.y,
            transform: state.placement === 'top'
              ? 'translate(-50%, -100%)'
              : 'translate(-50%, 0)',
            background: '#1C3D2E',
            color: '#fff',
            fontSize: 12,
            fontWeight: 500,
            padding: '7px 11px',
            '--ch': '5px',
            whiteSpace: 'pre-line',
            wordBreak: 'break-word',
            width: 'max-content',
            maxWidth: String(text).length > 200 ? 340 : 260,
            lineHeight: 1.35,
            boxShadow: '0 4px 12px rgba(28,61,46,0.18)',
            zIndex: 10000,
            pointerEvents: 'none',
            letterSpacing: '0.01em',
            textAlign: 'left',
          }}
        >
          {text}
          <span
            style={{
              position: 'absolute',
              left: `calc(50% + ${state.arrowShift}px)`,
              transform: 'translateX(-50%)',
              [state.placement === 'top' ? 'bottom' : 'top']: -4,
              width: 0,
              height: 0,
              borderLeft: '5px solid transparent',
              borderRight: '5px solid transparent',
              [state.placement === 'top' ? 'borderTop' : 'borderBottom']: '5px solid #1C3D2E',
            }}
          />
        </span>,
        document.body
      )}
    </>
  );
}

export default Tooltip;
